import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';

interface HubsEditorProps {
  availablePoints: { id: string; name: string }[];
  hubs: any[];
  setHubs: (hubs: any[]) => void;
}

const HubsEditor: React.FC<HubsEditorProps> = ({ availablePoints, hubs, setHubs }) => {
  const { t } = useTranslation('mapWizards');
  const [selectedPoint, setSelectedPoint] = useState<string>('');
  const total = hubs.reduce((sum, h) => sum + (h.percentage || 0), 0);
  const error = hubs.length > 2 && Math.abs(total - 100) > 0.01;
  const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  const addHub = () => {
    if (!selectedPoint) return;
    const point = availablePoints.find(p => p.id === selectedPoint);
    if (!point || hubs.some(h => h.id === point.id)) return;
    let newHubs: any[] = [...hubs, { id: point.id, name: point.name, percentage: 0 }];
    if (newHubs.length === 1) {
      newHubs[0].percentage = 100;
    } else if (newHubs.length === 2) {
      newHubs = newHubs.map(() => ({ ...point, percentage: 50 }));
      newHubs[0].id = hubs[0].id; newHubs[0].name = hubs[0].name;
      newHubs[1].id = point.id; newHubs[1].name = point.name;
    }
    setHubs(newHubs);
    setSelectedPoint('');
  };

  const handlePercentChange = (idx: number, value: string | number) => {
    let v = typeof value === 'string' ? parseFloat(value) : value;
    v = Math.max(0, Math.min(100, v));
    let newHubs = [...hubs];
    if (hubs.length === 2) {
      newHubs[idx].percentage = v;
      newHubs[1 - idx].percentage = 100 - v;
    } else {
      newHubs[idx].percentage = v;
    }
    setHubs(newHubs);
  };

  const removeHub = (idx: number) => {
    let newHubs = hubs.filter((_: any, i: number) => i !== idx);
    if (newHubs.length === 1) newHubs[0].percentage = 100;
    if (newHubs.length === 2) newHubs = newHubs.map(h => ({ ...h, percentage: 50 }));
    setHubs(newHubs);
  };

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="subtitle1">{t('generateRides.hubs.title')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('generateRides.hubs.selectHub')}</InputLabel>
          <Select
            value={selectedPoint}
            label={t('generateRides.hubs.selectHub')}
            onChange={e => setSelectedPoint(e.target.value)}
          >
            {availablePoints.filter(p => !hubs.some((h: any) => h.id === p.id)).map(p => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={addHub} disabled={!selectedPoint}>{t('generateRides.hubs.addHub')}</Button>
      </Box>
      {hubs.map((hub: any, idx: number) => (
        <Box key={hub.id} display="flex" alignItems="center" gap={2} mb={1}>
          <Typography sx={{ minWidth: 120 }}>{hub.name}</Typography>
          <TextField
            label={t('generateRides.hubs.percentOfRides')}
            type="number"
            value={hub.percentage}
            onChange={e => handlePercentChange(idx, e.target.value)}
            onWheel={preventWheelValueChange}
            size="small"
            sx={{ width: 100 }}
            inputProps={{ min: 0, max: 100 }}
            error={error}
          />
          <Button color="error" onClick={() => removeHub(idx)}>{t('generateRides.hubs.remove')}</Button>
        </Box>
      ))}
      {error && <Typography color="error">{t('generateRides.hubs.percentageError')}</Typography>}
    </Box>
  );
};

export default HubsEditor; 
